// Designer/admin-only authoring page for cabinet classes.
//
// Layout: left rail lists every class (system + user-authored), right
// pane edits the selected one. The editor itself is in
// `panels/CabinetClassEditor.tsx` — tabbed Form view (Header / Params /
// Parts) plus a JSON fallback. System classes load read-only.
// Save persists into the `cabinet_classes` ddb collection and updates
// the in-memory registry so all open projects re-render.

import { useEffect, useMemo, useState } from "react";

import { getCachedWhoAmI } from "@/lib/ddb";
import { useCreate, useRemove, useUpdate } from "@/lib/ddb-hooks";
import { useCabinetClasses, useCabinetClassesStore } from "@/lib/cabinets/dsl/registry";
import { evaluateClass } from "@/lib/cabinets/dsl/evaluate";
import type { Id, ParametricCabinetClass } from "@/lib/types";
import { CabinetClassEditor } from "@/panels/CabinetClassEditor";

export function CabinetClassesPage() {
  const who = getCachedWhoAmI();
  const canEdit = who?.isAdmin || who?.role === "designer";

  // Live list (registry already merges embedded + ddb).
  const { source } = useCabinetClasses();
  const list = useMemo(
    () => Object.values(source).sort((a, b) => a.label.localeCompare(b.label)),
    [source],
  );

  const [selectedId, setSelectedId] = useState<string | null>(list[0]?.id ?? null);
  const selected = selectedId ? source[selectedId] : null;

  if (!canEdit) {
    return (
      <div className="empty-state">
        Cabinet classes can only be edited by a designer or admin.
      </div>
    );
  }

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "280px 1fr",
      height: "100%",
      borderTop: "1px solid var(--line)",
    }}>
      <ClassList list={list} selectedId={selectedId} onSelect={setSelectedId} />
      <ClassEditor klass={selected ?? null} onSelect={setSelectedId} />
    </div>
  );
}

// ── List rail ───────────────────────────────────────────────────

function ClassList({
  list,
  selectedId,
  onSelect,
}: {
  list: ParametricCabinetClass[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const ddbCreate = useCreate<ParametricCabinetClass>("cabinet_classes");
  const store = useCabinetClassesStore();

  async function cloneInto(srcId: string) {
    const src = store.source[srcId];
    if (!src) return;
    const stamp = new Date().toISOString();
    const next: ParametricCabinetClass = {
      ...src,
      _id: undefined,
      id: `${src.id}_copy_${Date.now().toString(36).slice(-4)}`,
      label: `${src.label} (copy)`,
      system: false,
      locked: false,
      createdAt: stamp,
      updatedAt: stamp,
    };
    try {
      const newId = await ddbCreate.mutateAsync(next as Partial<ParametricCabinetClass>);
      store.upsert({ ...next, _id: newId as unknown as Id });
      onSelect(next.id);
    } catch (e) {
      // ddb collection may not yet exist on this deployment; fall back
      // to a registry-only add so the user can still play with the JSON.
      store.upsert(next);
      onSelect(next.id);
      console.warn("cabinet_classes collection not available; class kept in registry only", e);
    }
  }

  return (
    <aside style={{ borderRight: "1px solid var(--line)", overflowY: "auto" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)" }}>
        <div className="label">CABINET CLASSES</div>
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Clone a system class to start a new one.
        </div>
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 8 }}>
        {list.map((k) => (
          <li key={k.id} style={{ marginBottom: 2 }}>
            <button
              onClick={() => onSelect(k.id)}
              style={{
                width: "100%", textAlign: "left", padding: "8px 10px",
                fontSize: 12,
                border: "1px solid transparent",
                background: selectedId === k.id ? "rgba(255,170,85,0.10)" : "transparent",
                borderColor: selectedId === k.id ? "var(--accent, #ffaa55)" : "transparent",
                borderRadius: 4,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
              }}
            >
              <span>{k.label}</span>
              <span className="muted" style={{ fontSize: 10 }}>
                {k.system ? "system" : "custom"}
              </span>
            </button>
            <div style={{ display: "flex", gap: 4, padding: "0 10px 4px" }}>
              <button
                className="ghost"
                style={{ fontSize: 10, padding: "2px 6px" }}
                onClick={() => cloneInto(k.id)}
                title="Clone this class as a starting point for a new one"
              >Clone</button>
            </div>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// ── Class editor ────────────────────────────────────────────────

function ClassEditor({
  klass,
  onSelect,
}: {
  klass: ParametricCabinetClass | null;
  onSelect: (id: string) => void;
}) {
  // Draft as a structured object; the form editor renders directly on
  // this shape. JSON-mode toggle inside CabinetClassEditor serialises
  // back and forth via JSON.stringify / JSON.parse.
  const [draft, setDraft] = useState<ParametricCabinetClass | null>(klass);
  const [previewW, setPreviewW] = useState<number>(klass?.defaults.w ?? 36);
  const [previewH, setPreviewH] = useState<number>(klass?.defaults.h ?? 34.5);
  const [previewD, setPreviewD] = useState<number>(klass?.defaults.d ?? 24);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const ddbUpdate = useUpdate<ParametricCabinetClass>("cabinet_classes");
  const ddbRemove = useRemove("cabinet_classes");
  const ddbCreate = useCreate<ParametricCabinetClass>("cabinet_classes");
  const store = useCabinetClassesStore();

  // Re-seed draft state when the user picks a different class on the rail.
  useEffect(() => {
    setDraft(klass);
    setSaveError(null);
    setPreviewW(klass?.defaults.w ?? 36);
    setPreviewH(klass?.defaults.h ?? 34.5);
    setPreviewD(klass?.defaults.d ?? 24);
  }, [klass?.id]);

  if (!klass || !draft) {
    return (
      <main style={{ padding: 24, color: "var(--fg-dim)" }}>
        Select a class on the left, or clone a system class to create a new one.
      </main>
    );
  }

  const isDirty = klass !== draft && JSON.stringify(klass) !== JSON.stringify(draft);

  async function save() {
    if (!draft) return;
    const parsed: ParametricCabinetClass = { ...draft, updatedAt: new Date().toISOString() };
    setSaving(true);
    setSaveError(null);
    try {
      if (parsed._id != null) {
        await ddbUpdate.mutateAsync({ id: parsed._id as Id, changes: parsed });
      } else {
        const newId = await ddbCreate.mutateAsync(parsed as Partial<ParametricCabinetClass>);
        parsed._id = newId as unknown as Id;
      }
      store.upsert(parsed);
      setDraft(parsed);
      onSelect(parsed.id);
    } catch (e) {
      // ddb unavailable — keep local-only so the user doesn't lose work.
      store.upsert(parsed);
      setDraft(parsed);
      setSaveError(e instanceof Error ? e.message : String(e));
      console.warn("save fell back to registry-only", e);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!klass || klass.system) return;
    if (!confirm(`Delete class "${klass.label}"? Existing cabinets that reference it will fail to render until you restore.`)) return;
    if (klass._id != null) {
      try { await ddbRemove.mutateAsync(klass._id); } catch (e) { console.warn(e); }
    }
    store.remove(klass.id);
  }

  // Live preview — evaluate the draft at the chosen dims. Same data
  // surfaces inline in ExprInput hints AND in the summary bar below.
  const previewResult = evaluateClass(
    { width: previewW, height: previewH, depth: previewD, classParams: {}, partOverrides: {} },
    draft,
  );
  const previewScope = { W: previewW, H: previewH, D: previewD };

  const readOnly = klass.system;

  return (
    <main style={{
      display: "grid",
      gridTemplateRows: "auto auto 1fr auto",
      gap: 10,
      padding: 16,
      overflow: "hidden",
      minHeight: 0,
    }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14 }}>{draft.label}</h3>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            id <code>{draft.id}</code> · {readOnly ? "system class (read-only seed)" : "user-authored"} · {draft.parts.length} parts
            {isDirty && <span style={{ color: "var(--accent, #ffaa55)", marginLeft: 8 }}>● unsaved</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!klass.system && (
            <button className="ghost" onClick={remove}
              style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>
              Delete
            </button>
          )}
          <button onClick={save} disabled={readOnly || saving || !isDirty}>
            {saving ? "Saving…" : draft._id != null ? "Save" : "Save (new)"}
          </button>
        </div>
      </header>

      <section style={{
        display: "flex", gap: 8, alignItems: "center", fontSize: 12,
        paddingBottom: 8, borderBottom: "1px solid var(--line)",
      }}>
        <span className="label" style={{ margin: 0 }}>Preview dims</span>
        <input type="number" step={0.5} value={previewW} onChange={(e) => setPreviewW(Number(e.target.value))} style={{ width: 70 }} title="Width (W)" />
        <input type="number" step={0.5} value={previewH} onChange={(e) => setPreviewH(Number(e.target.value))} style={{ width: 70 }} title="Height (H)" />
        <input type="number" step={0.5} value={previewD} onChange={(e) => setPreviewD(Number(e.target.value))} style={{ width: 70 }} title="Depth (D)" />
        <span className="muted" style={{ fontSize: 11 }}>
          → {previewResult.parts.length} parts · {previewResult.meshes.length} mesh chunks ·{" "}
          {Object.values(previewResult.drillings).reduce((acc, ds) => acc + ds.length, 0)} drillings
          {previewResult.issues.length > 0 && (
            <span style={{ color: "var(--danger)", marginLeft: 8 }}>
              · {previewResult.issues.length} issue(s)
            </span>
          )}
        </span>
      </section>

      <CabinetClassEditor
        klass={draft}
        readOnly={readOnly}
        previewScope={previewScope}
        onChange={setDraft}
      />

      <footer style={{ display: "flex", gap: 12, fontSize: 11, color: "var(--fg-dim)", paddingTop: 8, borderTop: "1px solid var(--line)" }}>
        {saveError && <span style={{ color: "var(--danger)" }}>{saveError}</span>}
        {previewResult.issues.length > 0 && (
          <details style={{ flex: 1 }}>
            <summary style={{ color: "var(--danger)", cursor: "pointer" }}>
              {previewResult.issues.length} evaluation issue(s)
            </summary>
            <ul style={{ marginTop: 6 }}>
              {previewResult.issues.map((iss, i) => (
                <li key={i}>{iss.partId ? <code>{iss.partId}</code> : null} {iss.message}</li>
              ))}
            </ul>
          </details>
        )}
      </footer>
    </main>
  );
}

